import type { ReportPickSliceContext } from "./reportPickListStorage";
import { segmentSliceContextFromSummaryTable } from "./reportPickSlicePolicy";
import {
  getConditionAnalysisRoot,
  parseCandleDirectionTradeDirectionKey,
  parseRegimeDirectionKey,
  parseRegimeDirectionTradeDirectionKey,
  parseSessionCandleDirectionKey,
  parseSessionMarketConditionCandleTradeDirectionKey,
  parseSessionMarketConditionKey,
  parseSessionMarketConditionTradeDirectionKey,
  parseSessionTradeDirectionKey,
} from "./conditionAnalysisSelectors";
import {
  evidencePointerToParts,
  getEvidenceDocumentRoot,
  normalizeJsonPointer,
  resolveEvidencePointer,
} from "./reportEvidencePointer";

export { evidencePointerToParts };

export type MarketContextBucketsTabValue =
  | "best-buckets"
  | "worst-buckets"
  | "best-situations"
  | "worst-situations";
import { extractManagementSnapshotFields } from "./tradeManagementReport";
import { exitPlacementSliceDisplayLabel } from "./exitPlacementDisplay";
import { formatDirectionBarsWindow } from "./marketRegimeDirectionLookbacks";

const DIRECTION_LONG_BARS_LABEL = formatDirectionBarsWindow("long");
const DIRECTION_SHORT_BARS_LABEL = formatDirectionBarsWindow("short");

function safeIdPart(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function resolveAnalysisEvidenceBlob(
  inputSnapshot: unknown,
  analysisEvidence?: unknown
): Record<string, unknown> | null {
  if (analysisEvidence && typeof analysisEvidence === "object" && !Array.isArray(analysisEvidence)) {
    return analysisEvidence as Record<string, unknown>;
  }
  if (inputSnapshot && typeof inputSnapshot === "object" && !Array.isArray(inputSnapshot)) {
    const embedded = (inputSnapshot as Record<string, unknown>).analysis_evidence;
    if (embedded && typeof embedded === "object" && !Array.isArray(embedded)) {
      return embedded as Record<string, unknown>;
    }
  }
  return null;
}

/** Merge report row `analysis_evidence` into snapshot shape used by Market tab + pointer resolution. */
export function mergeReportEvidenceSources(
  inputSnapshot: unknown,
  analysisEvidence?: unknown
): unknown {
  const ae = resolveAnalysisEvidenceBlob(inputSnapshot, analysisEvidence);
  if (!ae) return inputSnapshot;
  if (!inputSnapshot || typeof inputSnapshot !== "object" || Array.isArray(inputSnapshot)) {
    return { analysis_evidence: ae };
  }
  const base = { ...(inputSnapshot as Record<string, unknown>) };
  if (!base.analysis_evidence) {
    base.analysis_evidence = ae;
  }
  const aeCa = ae.condition_analysis;
  if (aeCa && typeof aeCa === "object") {
    const stats =
      base.stats && typeof base.stats === "object" && !Array.isArray(base.stats)
        ? { ...(base.stats as Record<string, unknown>) }
        : {};
    const statsCa = stats.condition_analysis;
    const statsCaEmpty =
      !statsCa ||
      (typeof statsCa === "object" && !Array.isArray(statsCa) && Object.keys(statsCa).length === 0);
    if (statsCaEmpty) {
      stats.condition_analysis = aeCa;
    }
    base.stats = stats;
  }
  return base;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Configurator + display metadata to focus the Market tab on TES-cited evidence. */
export type MarketContextNavigationTarget = {
  label: string;
  configRegime: string;
  configCandleDirection: "" | "up" | "down" | "flat";
  configCandleWindow: "" | "long" | "short";
  configTradeDirection: "" | "long" | "short";
  configSession: string;
  /** Preferred drilldown slice id when known (e.g. `regime_trend`). */
  preferredSliceId?: string;
  summaryTableKey?: string;
  summaryRowKey?: string;
  /** Buckets / situations sub-tab on Market → All trades. */
  marketBucketsTab?: MarketContextBucketsTabValue;
  /** Row to highlight in situations tables (`situation` string, e.g. `consistency=low`). */
  highlightSituation?: string;
  /** Row to highlight in bucket tables (`bucket` string from condition analysis). */
  highlightBucket?: string;
};

type ActionableSituationRow = {
  situation?: string;
  clauses?: Array<{ key?: string; value?: string }>;
};

export function getRegimeActionableFilters(snapshot: unknown): {
  best_situations?: ActionableSituationRow[];
  worst_situations?: ActionableSituationRow[];
} | null {
  const doc = getEvidenceDocumentRoot(snapshot);
  if (!doc || !isPlainRecord(doc)) return null;
  const stats = isPlainRecord(doc.stats) ? doc.stats : null;
  if (!stats) return null;
  const raw = stats.regime_actionable_filters;
  if (!raw || !isPlainRecord(raw)) return null;
  return raw as {
    best_situations?: ActionableSituationRow[];
    worst_situations?: ActionableSituationRow[];
  };
}

function emptyConfiguratorFields(): Pick<
  MarketContextNavigationTarget,
  "configRegime" | "configCandleDirection" | "configCandleWindow" | "configTradeDirection" | "configSession"
> {
  return {
    configRegime: "",
    configCandleDirection: "",
    configCandleWindow: "",
    configTradeDirection: "",
    configSession: "",
  };
}

function targetFromRegimeActionableFilters(
  snapshot: unknown,
  listKey: "best_situations" | "worst_situations",
  index: number
): MarketContextNavigationTarget | null {
  const filters = getRegimeActionableFilters(snapshot);
  const list = filters?.[listKey];
  const row = Array.isArray(list) ? list[index] : undefined;
  if (!row) return null;
  const situation = String(row.situation ?? "").trim();
  const tab: MarketContextBucketsTabValue =
    listKey === "best_situations" ? "best-situations" : "worst-situations";
  const listLabel = listKey === "best_situations" ? "Best situations" : "Worst situations";
  return {
    label: `Actionable filters · ${listLabel} · ${situation.replace(/\|/g, " | ") || `#${index + 1}`}`,
    ...emptyConfiguratorFields(),
    preferredSliceId: "all",
    marketBucketsTab: tab,
    highlightSituation: situation || undefined,
  };
}

function targetFromMarketConditionBracketsList(
  snapshot: unknown,
  listKey: "best_situations" | "worst_situations",
  index: number
): MarketContextNavigationTarget | null {
  const root = getConditionAnalysisRoot(snapshot);
  const list = root?.market_condition_brackets?.[listKey];
  const row = Array.isArray(list) ? list[index] : undefined;
  if (!row) return null;
  const situation = String(row.situation ?? "").trim();
  const tab: MarketContextBucketsTabValue =
    listKey === "best_situations" ? "best-situations" : "worst-situations";
  const listLabel = listKey === "best_situations" ? "Best situations" : "Worst situations";
  return {
    label: `${listLabel} · ${situation.replace(/\|/g, " | ") || `#${index + 1}`}`,
    ...emptyConfiguratorFields(),
    preferredSliceId: "all",
    marketBucketsTab: tab,
    highlightSituation: situation || undefined,
  };
}

function targetFromConditionAnalysisBucketList(
  snapshot: unknown,
  listKey: "best_conditions" | "worst_conditions",
  index: number
): MarketContextNavigationTarget | null {
  const root = getConditionAnalysisRoot(snapshot);
  const list = root?.[listKey];
  const row = Array.isArray(list) ? list[index] : undefined;
  if (!row) return null;
  const bucket = String(row.bucket ?? "").trim();
  const tab: MarketContextBucketsTabValue =
    listKey === "best_conditions" ? "best-buckets" : "worst-buckets";
  const listLabel = listKey === "best_conditions" ? "Best buckets" : "Worst buckets";
  return {
    label: `${listLabel} · ${bucket || `#${index + 1}`}`,
    ...emptyConfiguratorFields(),
    preferredSliceId: "all",
    marketBucketsTab: tab,
    highlightBucket: bucket || undefined,
  };
}

function targetFromSituationEvidenceRow(
  row: unknown,
  listKey: "best_situations" | "worst_situations",
  labelPrefix: string,
  segmentTarget?: MarketContextNavigationTarget | null
): MarketContextNavigationTarget | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const situation = String((row as { situation?: string }).situation ?? "").trim();
  if (!situation) return null;
  const tab: MarketContextBucketsTabValue =
    listKey === "best_situations" ? "best-situations" : "worst-situations";
  const listLabel = listKey === "best_situations" ? "Best situations" : "Worst situations";
  return {
    ...(segmentTarget ?? { label: listLabel, ...emptyConfiguratorFields(), preferredSliceId: "all" }),
    label: `${labelPrefix} · ${situation.replace(/\|/g, " | ")}`,
    preferredSliceId: segmentTarget?.preferredSliceId ?? "all",
    marketBucketsTab: tab,
    highlightSituation: situation,
  };
}

const REGIME_SUMMARY_TABLE_ALIASES: Record<string, string> = {
  worst_market_condition_x_direction_short_x_trade_detail:
    "market_condition_candle_direction_short_and_trade_direction",
  worst_market_condition_x_direction_x_trade_detail:
    "market_condition_candle_direction_and_trade_direction",
};

function titleCaseWords(s: string): string {
  return s
    .split(/[\s_|]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function sliceContextToConfiguratorFields(ctx: ReportPickSliceContext): Pick<
  MarketContextNavigationTarget,
  "configRegime" | "configCandleDirection" | "configCandleWindow" | "configTradeDirection" | "configSession"
> {
  const configRegime = (ctx.market_condition ?? "").trim().toLowerCase();
  const cd = (ctx.candle_direction ?? "").trim().toLowerCase();
  const configCandleDirection =
    cd === "up" || cd === "down" || cd === "flat" ? (cd as "up" | "down" | "flat") : "";
  const td = ctx.trade_direction;
  const configTradeDirection = td === "long" || td === "short" ? td : "";
  const configSession = (ctx.session_id ?? "").trim();

  let configCandleWindow: "" | "long" | "short" = "";
  if (ctx.triple_candle_window === "long" || ctx.triple_candle_window === "short") {
    configCandleWindow = ctx.triple_candle_window;
  } else if (
    ctx.kind === "market_direction_short" ||
    ctx.kind === "candle_direction_short" ||
    ctx.kind === "candle_direction_short_trade_direction"
  ) {
    configCandleWindow = "short";
  } else if (
    ctx.kind === "market_direction" ||
    ctx.kind === "candle_direction" ||
    ctx.kind === "candle_direction_trade_direction"
  ) {
    configCandleWindow = "long";
  } else if (
    ctx.kind === "session_candle_direction_short" ||
    ctx.kind === "session_regime_direction_short_trade_direction"
  ) {
    configCandleWindow = "short";
  } else if (ctx.kind === "session_candle_direction" || ctx.kind === "session_regime_direction_trade_direction") {
    configCandleWindow = "long";
  }

  return { configRegime, configCandleDirection, configCandleWindow, configTradeDirection, configSession };
}

function targetFromSliceContext(ctx: ReportPickSliceContext, labelPrefix: string): MarketContextNavigationTarget {
  const fields = sliceContextToConfiguratorFields(ctx);
  const label =
    ctx.label?.trim() ||
    [labelPrefix, ctx.market_condition, ctx.candle_direction, ctx.trade_direction, ctx.session_id]
      .filter(Boolean)
      .join(" · ");
  return {
    label,
    ...fields,
    preferredSliceId: ctx.sliceId,
    summaryTableKey: undefined,
    summaryRowKey: undefined,
  };
}

function rowKeyFromEvidenceValue(value: unknown): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const row = value as Record<string, unknown>;
  const key = row.key ?? row.segment_key ?? row.segmentKey;
  return typeof key === "string" ? key.trim() : "";
}

function sliceIdFromRegimeBreakdown(branch: string, segmentKey: string): string | null {
  const key = segmentKey.trim();
  if (!key) return null;

  switch (branch) {
    case "by_market_condition":
      return `regime_${safeIdPart(key)}`;
    case "by_market_direction":
      return `mkt_dir_${safeIdPart(key)}`;
    case "by_market_direction_short":
      return `mkt_dir_short_${safeIdPart(key)}`;
    case "by_candle_direction":
      return `candle_dir_${safeIdPart(key)}`;
    case "by_candle_direction_short":
      return `candle_dir_short_${safeIdPart(key)}`;
    case "by_market_condition_and_direction_and_trade_direction": {
      const p = parseRegimeDirectionTradeDirectionKey(key);
      if (!p) return null;
      return `regime_dir_td_${safeIdPart(p.market_condition)}_${safeIdPart(p.candle_direction)}_${p.trade_direction}`;
    }
    case "by_market_condition_and_direction_short_and_trade_direction": {
      const p = parseRegimeDirectionTradeDirectionKey(key);
      if (!p) return null;
      return `regime_dir_short_td_${safeIdPart(p.market_condition)}_${safeIdPart(p.candle_direction)}_${p.trade_direction}`;
    }
    case "by_candle_direction_and_trade_direction": {
      const p = parseCandleDirectionTradeDirectionKey(key);
      if (!p) return null;
      return `cd_td_${safeIdPart(p.candle_direction)}_${p.trade_direction}`;
    }
    case "by_candle_direction_short_and_trade_direction": {
      const p = parseCandleDirectionTradeDirectionKey(key);
      if (!p) return null;
      return `cd_short_td_${safeIdPart(p.candle_direction)}_${p.trade_direction}`;
    }
    case "by_session":
      return `session_${safeIdPart(key)}`;
    case "by_session_and_trade_direction": {
      const p = parseSessionTradeDirectionKey(key);
      if (!p) return null;
      return `sess_td_${safeIdPart(p.session_id)}_${p.trade_direction}`;
    }
    case "by_session_and_candle_direction": {
      const p = parseSessionCandleDirectionKey(key);
      if (!p) return null;
      return `sess_cd_${safeIdPart(p.session_id)}_${p.candle_direction}`;
    }
    case "by_session_and_candle_direction_short": {
      const p = parseSessionCandleDirectionKey(key);
      if (!p) return null;
      return `sess_cds_${safeIdPart(p.session_id)}_${p.candle_direction}`;
    }
    case "by_session_and_market_condition": {
      const p = parseSessionMarketConditionKey(key);
      if (!p) return null;
      return `sess_mc_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}`;
    }
    case "by_session_and_market_condition_and_trade_direction": {
      const p = parseSessionMarketConditionTradeDirectionKey(key);
      if (!p) return null;
      return `sess_mctd_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}_${p.trade_direction}`;
    }
    case "by_session_and_market_condition_and_direction_and_trade_direction": {
      const p = parseSessionMarketConditionCandleTradeDirectionKey(key);
      if (!p) return null;
      return `sess_mcd_td_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}_${p.candle_direction}_${p.trade_direction}`;
    }
    case "by_session_and_market_condition_and_direction_short_and_trade_direction": {
      const p = parseSessionMarketConditionCandleTradeDirectionKey(key);
      if (!p) return null;
      return `sess_mcdstd_${safeIdPart(p.session_id)}_${safeIdPart(p.market_condition)}_${p.candle_direction}_${p.trade_direction}`;
    }
    default:
      return null;
  }
}

function targetFromRegimeBreakdown(branch: string, segmentKey: string): MarketContextNavigationTarget | null {
  const preferredSliceId = sliceIdFromRegimeBreakdown(branch, segmentKey);
  const key = segmentKey.trim();

  const branchLabels: Record<string, string> = {
    by_market_condition: "Regime",
    by_market_direction: `Market direction (${DIRECTION_LONG_BARS_LABEL})`,
    by_market_direction_short: `Market direction (${DIRECTION_SHORT_BARS_LABEL})`,
    by_candle_direction: `Candle direction (${DIRECTION_LONG_BARS_LABEL})`,
    by_candle_direction_short: `Candle direction (${DIRECTION_SHORT_BARS_LABEL})`,
    by_market_condition_and_direction_and_trade_direction: `Regime × direction × side (${DIRECTION_LONG_BARS_LABEL})`,
    by_market_condition_and_direction_short_and_trade_direction: `Regime × direction × side (${DIRECTION_SHORT_BARS_LABEL})`,
    by_candle_direction_and_trade_direction: `Candle × side (${DIRECTION_LONG_BARS_LABEL})`,
    by_candle_direction_short_and_trade_direction: `Candle × side (${DIRECTION_SHORT_BARS_LABEL})`,
    by_session: "Session",
    by_session_and_trade_direction: "Session × side",
    by_session_and_candle_direction: `Session × candle (${DIRECTION_LONG_BARS_LABEL})`,
    by_session_and_candle_direction_short: `Session × candle (${DIRECTION_SHORT_BARS_LABEL})`,
    by_session_and_market_condition: "Session × regime",
    by_session_and_market_condition_and_trade_direction: "Session × regime × side",
    by_session_and_market_condition_and_direction_and_trade_direction: `Session × regime × candle × side (${DIRECTION_LONG_BARS_LABEL})`,
    by_session_and_market_condition_and_direction_short_and_trade_direction: `Session × regime × candle × side (${DIRECTION_SHORT_BARS_LABEL})`,
  };

  const label = `${branchLabels[branch] ?? "Market segment"} · ${key.replace(/\|/g, " | ")}`;

  if (branch === "by_market_condition") {
    return {
      label,
      configRegime: key.toLowerCase(),
      configCandleDirection: "",
      configCandleWindow: "",
      configTradeDirection: "",
      configSession: "",
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_market_direction" || branch === "by_candle_direction") {
    const cd = key.toLowerCase();
    return {
      label,
      configRegime: "",
      configCandleDirection:
        cd === "up" || cd === "down" || cd === "flat" ? (cd as "up" | "down" | "flat") : "",
      configCandleWindow: "long",
      configTradeDirection: "",
      configSession: "",
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_market_direction_short" || branch === "by_candle_direction_short") {
    const cd = key.toLowerCase();
    return {
      label,
      configRegime: "",
      configCandleDirection:
        cd === "up" || cd === "down" || cd === "flat" ? (cd as "up" | "down" | "flat") : "",
      configCandleWindow: "short",
      configTradeDirection: "",
      configSession: "",
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_market_condition_and_direction_and_trade_direction") {
    const p = parseRegimeDirectionTradeDirectionKey(key);
    if (!p) return null;
    return {
      label,
      configRegime: p.market_condition.toLowerCase(),
      configCandleDirection:
        p.candle_direction === "up" || p.candle_direction === "down" || p.candle_direction === "flat"
          ? p.candle_direction
          : "",
      configCandleWindow: "long",
      configTradeDirection: p.trade_direction,
      configSession: "",
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_market_condition_and_direction_short_and_trade_direction") {
    const p = parseRegimeDirectionTradeDirectionKey(key);
    if (!p) return null;
    return {
      label,
      configRegime: p.market_condition.toLowerCase(),
      configCandleDirection:
        p.candle_direction === "up" || p.candle_direction === "down" || p.candle_direction === "flat"
          ? p.candle_direction
          : "",
      configCandleWindow: "short",
      configTradeDirection: p.trade_direction,
      configSession: "",
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_candle_direction_and_trade_direction" || branch === "by_candle_direction_short_and_trade_direction") {
    const p = parseCandleDirectionTradeDirectionKey(key);
    if (!p) return null;
    return {
      label,
      configRegime: "",
      configCandleDirection:
        p.candle_direction === "up" || p.candle_direction === "down" || p.candle_direction === "flat"
          ? p.candle_direction
          : "",
      configCandleWindow: branch.includes("short") ? "short" : "long",
      configTradeDirection: p.trade_direction,
      configSession: "",
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_session") {
    return {
      label,
      configRegime: "",
      configCandleDirection: "",
      configCandleWindow: "",
      configTradeDirection: "",
      configSession: key,
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  if (branch === "by_session_and_trade_direction") {
    const p = parseSessionTradeDirectionKey(key);
    if (!p) return null;
    return {
      label,
      configRegime: "",
      configCandleDirection: "",
      configCandleWindow: "",
      configTradeDirection: p.trade_direction,
      configSession: p.session_id,
      preferredSliceId: preferredSliceId ?? undefined,
    };
  }

  const ctx = segmentSliceContextFromSummaryTable(
    branch.replace(/^by_session_and_/, "session_and_").replace(/^by_/, ""),
    key
  );
  if (ctx) {
    const t = targetFromSliceContext(ctx, branchLabels[branch] ?? "Market segment");
    return { ...t, label, preferredSliceId: preferredSliceId ?? t.preferredSliceId };
  }

  return {
    label,
    configRegime: "",
    configCandleDirection: "",
    configCandleWindow: "",
    configTradeDirection: "",
    configSession: "",
    preferredSliceId: preferredSliceId ?? undefined,
  };
}

function targetFromSegmentKeyAndScope(
  segmentKey: string,
  segmentScope?: string
): MarketContextNavigationTarget | null {
  const key = segmentKey.trim();
  if (!key) return null;
  const scope = String(segmentScope ?? "").trim().toLowerCase();

  if (scope === "triple_short" || scope.includes("short")) {
    return targetFromRegimeBreakdown("by_market_condition_and_direction_short_and_trade_direction", key);
  }
  if (scope === "triple" || key.split("|").length >= 3) {
    const shortBranch = targetFromRegimeBreakdown(
      "by_market_condition_and_direction_short_and_trade_direction",
      key
    );
    const longBranch = targetFromRegimeBreakdown(
      "by_market_condition_and_direction_and_trade_direction",
      key
    );
    return shortBranch ?? longBranch;
  }
  if (scope === "market_condition" || (!scope && !key.includes("|"))) {
    return targetFromRegimeBreakdown("by_market_condition", key);
  }

  const triple = parseRegimeDirectionTradeDirectionKey(key);
  if (triple) {
    return targetFromRegimeBreakdown("by_market_condition_and_direction_and_trade_direction", key);
  }
  const pair = parseRegimeDirectionKey(key);
  if (pair) {
    const ctx = segmentSliceContextFromSummaryTable("market_condition_and_direction", key);
    if (ctx) return targetFromSliceContext(ctx, "Segment");
  }
  return null;
}

function targetFromManagementCandidateIndex(
  snapshot: unknown,
  index: number
): MarketContextNavigationTarget | null {
  const { candidates } = extractManagementSnapshotFields(snapshot);
  const row = candidates[index];
  if (!row || !isPlainRecord(row)) return null;
  const segmentKey = String((row as Record<string, unknown>).segment_key ?? "").trim();
  const segmentScope = String((row as Record<string, unknown>).segment_scope ?? "").trim();
  const target = targetFromSegmentKeyAndScope(segmentKey, segmentScope);
  if (target) {
    return {
      ...target,
      label: `Management segment · ${segmentKey.replace(/\|/g, " | ")}`,
    };
  }
  return null;
}

function regimeSummaryRowKeyFromIndex(
  snapshot: unknown,
  tableKey: string,
  index: number
): string {
  const root = getConditionAnalysisRoot(snapshot);
  if (!root?.regime_summary) return "";
  const rows = (root.regime_summary as Record<string, unknown>)[tableKey];
  if (!Array.isArray(rows)) return "";
  const row = rows[index];
  if (!row || typeof row !== "object") return "";
  const key = (row as { key?: string }).key;
  return typeof key === "string" ? key.trim() : "";
}

function targetFromImpactDigestSourcePath(row: Record<string, unknown>): MarketContextNavigationTarget | null {
  const sourcePath = String(row.source_path ?? "").trim();
  if (!sourcePath) return null;

  const situation = String(row.situation ?? "").trim();
  const bucket = String(row.bucket ?? "").trim();
  const label = String(row.label ?? "").trim();
  const segmentContext = String(row.segment_context ?? "").trim();
  const display = (situation || bucket || label).replace(/\|/g, " | ");

  if (sourcePath.includes("worst_situations")) {
    return {
      label: `Impact leak · ${display}`,
      ...emptyConfiguratorFields(),
      preferredSliceId: "all",
      marketBucketsTab: "worst-situations",
      highlightSituation: situation || label || undefined,
    };
  }
  if (sourcePath.includes("best_situations")) {
    return {
      label: `Impact leak · ${display}`,
      ...emptyConfiguratorFields(),
      preferredSliceId: "all",
      marketBucketsTab: "best-situations",
      highlightSituation: situation || label || undefined,
    };
  }
  if (sourcePath.includes("worst_conditions") && !sourcePath.includes("regime_breakdown")) {
    return {
      label: `Impact leak · ${display}`,
      ...emptyConfiguratorFields(),
      preferredSliceId: "all",
      marketBucketsTab: "worst-buckets",
      highlightBucket: bucket || label || undefined,
    };
  }
  if (sourcePath.includes("best_conditions") && !sourcePath.includes("regime_breakdown")) {
    return {
      label: `Impact leak · ${display}`,
      ...emptyConfiguratorFields(),
      preferredSliceId: "all",
      marketBucketsTab: "best-buckets",
      highlightBucket: bucket || label || undefined,
    };
  }

  const regimeBreakdown = sourcePath.match(/regime_breakdown\.([^.]+)/);
  if (regimeBreakdown && segmentContext) {
    const branch = regimeBreakdown[1];
    const segmentTarget = targetFromRegimeBreakdown(branch, segmentContext);
    if (!segmentTarget) return null;
    if (bucket) {
      return {
        ...segmentTarget,
        label: `${segmentTarget.label} · ${bucket}`,
        highlightBucket: bucket,
      };
    }
    if (situation) {
      return {
        ...segmentTarget,
        label: `${segmentTarget.label} · ${situation.replace(/\|/g, " | ")}`,
        highlightSituation: situation,
      };
    }
    return segmentTarget;
  }

  return null;
}

function targetFromDigestRow(value: unknown): MarketContextNavigationTarget | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;

  const fromSourcePath = targetFromImpactDigestSourcePath(row);
  if (fromSourcePath) return fromSourcePath;

  const sliceId = String(row.slice_id ?? "").trim();
  if (sliceId) {
    const segmentLabel = exitPlacementSliceDisplayLabel(
      row as Parameters<typeof exitPlacementSliceDisplayLabel>[0]
    );
    return {
      label: segmentLabel ? `Exit placement · ${segmentLabel}` : `Exit placement · ${sliceId}`,
      configRegime: "",
      configCandleDirection: "",
      configCandleWindow: "",
      configTradeDirection: "",
      configSession: "",
      preferredSliceId: sliceId,
    };
  }

  const segmentKey = String(row.segment_key ?? row.segmentKey ?? "").trim();
  const breakdownPath = String(row.breakdown_path ?? row.breakdownPath ?? "").trim();

  if (breakdownPath.startsWith("regime_breakdown.")) {
    const rest = breakdownPath.slice("regime_breakdown.".length);
    const dot = rest.indexOf(".");
    const branch = dot >= 0 ? rest.slice(0, dot) : rest;
    const keyFromPath = dot >= 0 ? rest.slice(dot + 1) : "";
    const segKey = segmentKey || keyFromPath;
    if (branch && segKey) {
      return targetFromRegimeBreakdown(branch, segKey);
    }
  }

  if (segmentKey) {
    const triple = parseRegimeDirectionTradeDirectionKey(segmentKey);
    if (triple) {
      return targetFromRegimeBreakdown("by_market_condition_and_direction_and_trade_direction", segmentKey);
    }
    const pair = parseRegimeDirectionKey(segmentKey);
    if (pair) {
      const ctx = segmentSliceContextFromSummaryTable("market_condition_and_direction", segmentKey);
      if (ctx) return targetFromSliceContext(ctx, "TES digest");
    }
    if (segmentKey.includes("|")) {
      return {
        label: `TES digest · ${segmentKey.replace(/\|/g, " | ")}`,
        ...sliceContextToConfiguratorFields({
          sliceId: "",
          kind: "regime",
          label: segmentKey,
          market_condition: segmentKey.split("|")[0],
        }),
      };
    }
    return {
      label: `TES digest · ${titleCaseWords(segmentKey)}`,
      configRegime: segmentKey.toLowerCase(),
      configCandleDirection: "",
      configCandleWindow: "",
      configTradeDirection: "",
      configSession: "",
    };
  }

  return null;
}

/** Resolve a TES evidence pointer to a Market-tab navigation target (Regime / market-context reports). */
export function resolveMarketContextNavigationTarget(
  snapshot: unknown,
  pointer: string
): MarketContextNavigationTarget | null {
  const parts = evidencePointerToParts(pointer);
  if (!parts.length) return null;

  if (parts[0] === "management_candidates" && parts[1] && /^\d+$/.test(parts[1])) {
    return targetFromManagementCandidateIndex(snapshot, Number(parts[1]));
  }

  let statsTail: string[];
  if (parts[0] === "stats") {
    statsTail = parts.slice(1);
  } else if (parts[0] === "analysis_evidence") {
    statsTail = parts.slice(1);
  } else if (parts[0] === "condition_analysis") {
    statsTail = ["condition_analysis", ...parts.slice(1)];
  } else {
    return null;
  }

  const normalized = `/stats/${statsTail.map((p) => p.replace(/~/g, "~0")).join("/")}`;

  if (statsTail[0] === "regime_actionable_filters" && statsTail.length >= 3) {
    const listKey = statsTail[1];
    if (
      (listKey === "best_situations" || listKey === "worst_situations") &&
      statsTail[2] &&
      /^\d+$/.test(statsTail[2])
    ) {
      return targetFromRegimeActionableFilters(
        snapshot,
        listKey,
        Number(statsTail[2])
      );
    }
  }

  if (statsTail[0] === "tes_condition_digest" && statsTail.length >= 2) {
    const digestLabels: Record<string, string> = {
      headline_worst_segments: "TES digest · worst segments",
      headline_worst_segments_short_horizon: "TES digest · worst segments (short horizon)",
      joint_marginal_hits: "TES digest · joint marginal",
      directional_headlines: "TES digest · directional headlines",
      impact_worst_leaks: "TES digest · impact worst leaks",
    };

    const nestedListKey = statsTail[statsTail.length - 2];
    const nestedIndex = statsTail[statsTail.length - 1];
    if (
      statsTail.length >= 4 &&
      (nestedListKey === "worst_situations" || nestedListKey === "best_situations") &&
      nestedIndex &&
      /^\d+$/.test(nestedIndex)
    ) {
      const value = resolveEvidencePointer(snapshot, normalized);
      const parentPointer = `/stats/${statsTail.slice(0, -2).join("/")}`;
      const parentRow = resolveEvidencePointer(snapshot, parentPointer);
      const segmentTarget = targetFromDigestRow(parentRow);
      const tableLabel = digestLabels[statsTail[1]] ?? "TES digest";
      const fromSituation = targetFromSituationEvidenceRow(
        value,
        nestedListKey,
        segmentTarget?.label ?? tableLabel,
        segmentTarget
      );
      if (fromSituation) return fromSituation;
    }

    if (
      statsTail[1] === "impact_worst_leaks" &&
      statsTail[2] &&
      /^\d+$/.test(statsTail[2])
    ) {
      const value = resolveEvidencePointer(snapshot, normalized);
      const fromRow = targetFromDigestRow(value);
      if (fromRow) {
        return fromRow.label.startsWith("Impact leak")
          ? fromRow
          : {
              ...fromRow,
              label: `${digestLabels.impact_worst_leaks} · ${fromRow.label.replace(/^TES digest ·\s*/, "")}`,
            };
      }
    }

    if (
      (statsTail[1] === "headline_worst_segments" ||
        statsTail[1] === "headline_worst_segments_short_horizon") &&
      statsTail[2] &&
      /^\d+$/.test(statsTail[2])
    ) {
      const headPointer = `/stats/tes_condition_digest/${statsTail[1]}/${statsTail[2]}`;
      const value = resolveEvidencePointer(snapshot, headPointer);
      const fromRow = targetFromDigestRow(value);
      if (fromRow) {
        return {
          ...fromRow,
          label: `${digestLabels[statsTail[1]]} · ${fromRow.label.replace(/^TES digest ·\s*/, "")}`,
        };
      }
    }

    if (statsTail.length >= 3) {
      const value = resolveEvidencePointer(snapshot, normalized);
      const fromRow = targetFromDigestRow(value);
      if (fromRow) {
        const table = statsTail[1];
        if (!fromRow.label.includes("TES digest") && digestLabels[table]) {
          return {
            ...fromRow,
            label: `${digestLabels[table]} · ${fromRow.label.replace(/^TES digest ·\s*/, "")}`,
          };
        }
        return fromRow;
      }
    }
  }

  if (statsTail[0] === "exit_placement_priority_leaks" && statsTail.length >= 2) {
    if (statsTail[1] === "leaks" && statsTail[2] && /^\d+$/.test(statsTail[2])) {
      const value = resolveEvidencePointer(snapshot, normalized);
      const fromRow = targetFromDigestRow(value);
      if (fromRow) {
        return fromRow.label.startsWith("Exit placement")
          ? fromRow
          : {
              ...fromRow,
              label: `Top leak context · ${fromRow.label.replace(/^Exit placement ·\s*/, "")}`,
            };
      }
    }
  }

  if (statsTail[0] !== "condition_analysis") return null;
  const ca = statsTail.slice(1);

  if (ca[0] === "regime_summary" && ca.length >= 3) {
    const rawTable = ca[1];
    const tableKey = REGIME_SUMMARY_TABLE_ALIASES[rawTable] ?? rawTable;
    const value = resolveEvidencePointer(snapshot, normalized);
    let rowKey = rowKeyFromEvidenceValue(value);
    if (!rowKey && /^\d+$/.test(ca[2] ?? "")) {
      rowKey = regimeSummaryRowKeyFromIndex(snapshot, tableKey, Number(ca[2]));
    }
    if (!rowKey) return null;
    const ctx = segmentSliceContextFromSummaryTable(tableKey, rowKey);
    if (!ctx) return null;
    const tableLabel = rawTable.replace(/_/g, " ");
    return {
      ...targetFromSliceContext(ctx, "Regime summary"),
      label: `Regime summary · ${tableLabel} · ${rowKey.replace(/\|/g, " | ")}`,
      summaryTableKey: tableKey,
      summaryRowKey: rowKey,
    };
  }

  if (ca[0] === "regime_breakdown" && ca.length >= 3) {
    return targetFromRegimeBreakdown(ca[1], ca[2]);
  }

  if (ca[0] === "directional_condition_analysis" && ca.length >= 2) {
    const side = ca[1];
    if (side === "long" || side === "short") {
      return {
        label: side === "long" ? "Long trades" : "Short trades",
        ...emptyConfiguratorFields(),
        configTradeDirection: side,
        preferredSliceId: side === "long" ? "dir_long" : "dir_short",
      };
    }
  }

  if (ca[0] === "market_condition_brackets" && ca.length >= 3) {
    const listKey = ca[1];
    if (
      (listKey === "best_situations" || listKey === "worst_situations") &&
      ca[2] &&
      /^\d+$/.test(ca[2])
    ) {
      return targetFromMarketConditionBracketsList(snapshot, listKey, Number(ca[2]));
    }
  }

  if (
    (ca[0] === "best_situations" || ca[0] === "worst_situations") &&
    ca[1] &&
    /^\d+$/.test(ca[1])
  ) {
    return targetFromMarketConditionBracketsList(snapshot, ca[0], Number(ca[1]));
  }

  if (
    (ca[0] === "best_conditions" || ca[0] === "worst_conditions") &&
    ca[1] &&
    /^\d+$/.test(ca[1])
  ) {
    return targetFromConditionAnalysisBucketList(snapshot, ca[0], Number(ca[1]));
  }

  return null;
}

/** First resolvable Market-tab target from a finding's evidence pointers. */
export function pickMarketContextNavigationTarget(
  snapshot: unknown,
  pointers: string[],
  analysisEvidence?: unknown
): MarketContextNavigationTarget | null {
  const merged = mergeReportEvidenceSources(snapshot, analysisEvidence);
  for (const pointer of pointers) {
    const target = resolveMarketContextNavigationTarget(merged, pointer);
    if (target) return target;
  }
  return null;
}
